<template>
  <div class="card">
    <!-- Header with optional title and actions -->
    <div v-if="showHeader" class="flex items-center justify-between mb-4">
      <h2 v-if="title" class="text-lg font-heading font-semibold text-mifo-primary">
        {{ title }}
      </h2>
      <div v-if="$slots.actions" class="flex space-x-2">
        <slot name="actions" />
      </div>
    </div>

    <!-- Filters (optional) -->
    <div v-if="showFilters" class="mb-4">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            v-model="filters.status"
            class="input-field"
          >
            <option value="">Alle</option>
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Stadt
          </label>
          <select
            v-model="filters.city"
            class="input-field"
          >
            <option value="">Alle Städte</option>
            <option 
              v-for="city in availableCities" 
              :key="city" 
              :value="city"
            >
              {{ city }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Datum von
          </label>
          <input
            v-model="filters.dateFrom"
            type="date"
            class="input-field"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Datum bis
          </label>
          <input
            v-model="filters.dateTo"
            type="date"
            class="input-field"
          />
        </div>
      </div>
    </div>

    <!-- Course Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button
                v-if="showSorting"
                @click="sortBy('title')"
                class="flex items-center hover:text-gray-700"
              >
                Kurs
                <ChevronUp v-if="sortField === 'title' && sortDirection === 'asc'" class="ml-1 h-4 w-4 inline-block align-middle" />
                <ChevronDown v-else-if="sortField === 'title' && sortDirection === 'desc'" class="ml-1 h-4 w-4 inline-block align-middle" />
              </button>
              <span v-else>Kurs</span>
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button
                v-if="showSorting"
                @click="sortBy('startDate')"
                class="flex items-center hover:text-gray-700"
              >
                Startdatum
                <ChevronUp v-if="sortField === 'startDate' && sortDirection === 'asc'" class="ml-1 h-4 w-4 inline-block align-middle" />
                <ChevronDown v-else-if="sortField === 'startDate' && sortDirection === 'desc'" class="ml-1 h-4 w-4 inline-block align-middle" />
              </button>
              <span v-else>Datum</span>
            </th>
            <th v-if="showEndDate" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <button
                v-if="showSorting"
                @click="sortBy('endDate')"
                class="flex items-center hover:text-gray-700"
              >
                Enddatum
                <ChevronUp v-if="sortField === 'endDate' && sortDirection === 'asc'" class="ml-1 h-4 w-4 inline-block align-middle" />
                <ChevronDown v-else-if="sortField === 'endDate' && sortDirection === 'desc'" class="ml-1 h-4 w-4 inline-block align-middle" />
              </button>
              <span v-else>Enddatum</span>
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Stadt
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Plätze
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th v-if="showActions" class="relative px-6 py-3">
              <span class="sr-only">Aktionen</span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr 
            v-for="course in filteredCourses" 
            :key="course.id"
            @click="handleCourseClick(course)"
            :class="[
              'transition-colors',
              clickable ? 'hover:bg-gray-50 cursor-pointer' : ''
            ]"
          >
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="text-sm font-medium text-mifo-primary">
                {{ course.title }}
              </div>
              <div class="text-sm text-gray-500">
                {{ showId ? `ID: ${course.id}` : course.city }}
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ formatDate(course.startDate) }}
            </td>
            <td v-if="showEndDate" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ formatDate(course.endDate) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {{ course.city }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              <div class="flex items-center">
                <span class="mr-2">{{ course.soldSpots }}/{{ course.totalSpots }}</span>
                <div class="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    class="bg-mifo-accent h-2 rounded-full"
                    :style="{ width: `${(course.soldSpots / course.totalSpots) * 100}%` }"
                  ></div>
                </div>
              </div>
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                :class="[
                  course.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ course.status === 'active' ? 'Aktiv' : 'Inaktiv' }}
              </span>
            </td>
            <td v-if="showActions" class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <slot name="actions" :course="course">
                <router-link
        :to="`/herzbeben/courses/${course.id}`"
                  class="text-mifo-accent hover:text-mifo-accent/80"
                >
                  Details
                </router-link>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { ChevronUp, ChevronDown } from 'lucide-vue-next'
import { formatDate } from 'shared/utils'

export default {
  name: 'CourseTable',
  components: {
    ChevronUp,
    ChevronDown
  },
  props: {
    courses: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFilters: {
      type: Boolean,
      default: false
    },
    showSorting: {
      type: Boolean,
      default: false
    },
    showEndDate: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: false
    },
    showId: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: true
    }
  },
  emits: ['course-click'],
  setup(props, { emit }) {
    const filters = ref({
      status: '',
      city: '',
      dateFrom: '',
      dateTo: ''
    })

    const sortField = ref('')
    const sortDirection = ref('asc')

    const availableCities = computed(() => {
      const cities = [...new Set(props.courses.map(course => course.city))]
      return cities.sort()
    })

    const filteredCourses = computed(() => {
      let filtered = [...props.courses]

      // Apply filters
      if (filters.value.status) {
        filtered = filtered.filter(course => course.status === filters.value.status)
      }

      if (filters.value.city) {
        filtered = filtered.filter(course => course.city === filters.value.city)
      }

      if (filters.value.dateFrom) {
        filtered = filtered.filter(course => course.startDate >= filters.value.dateFrom)
      }

      if (filters.value.dateTo) {
        filtered = filtered.filter(course => course.startDate <= filters.value.dateTo)
      }

      // Apply sorting
      if (sortField.value && sortDirection.value) {
        filtered.sort((a, b) => {
          let aVal = a[sortField.value]
          let bVal = b[sortField.value]

          if (sortField.value === 'title') {
            aVal = aVal.toLowerCase()
            bVal = bVal.toLowerCase()
          }

          if (sortDirection.value === 'asc') {
            return aVal > bVal ? 1 : -1
          } else {
            return aVal < bVal ? 1 : -1
          }
        })
      }

      return filtered
    })

    const sortBy = (field) => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortDirection.value = 'asc'
      }
    }

    const handleCourseClick = (course) => {
      if (props.clickable) {
        emit('course-click', course)
      }
    }

    return {
      filters,
      sortField,
      sortDirection,
      availableCities,
      filteredCourses,
      sortBy,
      handleCourseClick,
      formatDate
    }
  }
}
</script> 